import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { captureRedirectUrl } from '../../utils/redirectUtils';
import { userRequiresPhone } from '../../utils/phoneGate';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  /** Skip phone completion gate (e.g. complete-phone page itself) */
  skipPhoneGate?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
  skipPhoneGate = false,
}) => {
  const { isAuthenticated, isLoading, initialize, setRedirectAfterLogin, user } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await initialize();
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    if (!isInitialized) {
      initializeAuth();
    }
  }, [initialize, isInitialized]);

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-m2-surface-low flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-m2-primary mx-auto"></div>
          <p className="mt-4 text-m2-on-surface-variant">Loading...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    const currentUrl = captureRedirectUrl(location as any);
    setRedirectAfterLogin(currentUrl);
    return <Navigate to={redirectTo} state={{ from: location as any }} replace />;
  }

  if (
    requireAuth &&
    isAuthenticated &&
    !skipPhoneGate &&
    location.pathname !== '/auth/complete-phone' &&
    userRequiresPhone(user)
  ) {
    setRedirectAfterLogin(captureRedirectUrl(location as any));
    return <Navigate to="/auth/complete-phone" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;